import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { useSnackbar } from 'src/components/snackbar';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { Iconify } from 'src/components/iconify';
import { PageContainer } from 'src/components/page-container';
import { useLocales } from 'src/locales';

import { generateCatalogPdf, useGetCatalogPdfJobs } from 'src/api/admin';
import { CATALOG_PDF_IN_FLIGHT_STATUSES, CatalogPdfJobStatus, ICatalogPdfJob } from 'src/types';

import CatalogPdfTableRow from '../list/catalog-pdf-table-row';

const JOBS_LIMIT = 10;
const POLL_INTERVAL_MS = 5000;

function isInFlight(status: CatalogPdfJobStatus): boolean {
  return CATALOG_PDF_IN_FLIGHT_STATUSES.includes(status);
}

export default function CatalogPdfView() {
  const { t } = useLocales('catalogPdf');
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, refetch } = useGetCatalogPdfJobs(JOBS_LIMIT);

  const jobs: ICatalogPdfJob[] = useMemo(() => data ?? [], [data]);

  const hasInFlightJob = useMemo(() => jobs.some((job) => isInFlight(job.status)), [jobs]);

  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!hasInFlightJob) return undefined;
    const id = setInterval(() => {
      refetch();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasInFlightJob, refetch]);

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true);
    try {
      await generateCatalogPdf();
      enqueueSnackbar(t('generate_success'), { variant: 'success' });
      await refetch();
    } catch (error: unknown) {
      const err = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 409) {
        enqueueSnackbar(message || t('in_flight_message'), { variant: 'warning' });
        await refetch();
      } else {
        enqueueSnackbar(message || t('generate_error'), { variant: 'error' });
      }
    } finally {
      setIsGenerating(false);
    }
  }, [enqueueSnackbar, refetch, t]);

  const buttonDisabled = isGenerating || hasInFlightJob;

  return (
    <PageContainer variant="data">
      <CustomBreadcrumbs
        heading={t('title')}
        links={[{ name: t('title') }]}
        action={
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={buttonDisabled}
            startIcon={
              isGenerating ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Iconify icon="eva:file-text-fill" />
              )
            }
          >
            {isGenerating || hasInFlightJob ? t('generating') : t('generate_button')}
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Card>
        <Box sx={{ px: { xs: 1, sm: 2.5 }, pt: 2.5, pb: 1 }}>
          <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary', mb: 1 }}>
            {t('recent_jobs')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('description')}
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
          <Table size="medium" sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('col_requested_at')}</TableCell>
                <TableCell>{t('col_status')}</TableCell>
                <TableCell>{t('col_trigger')}</TableCell>
                <TableCell>{t('col_requested_by')}</TableCell>
                <TableCell>{t('col_counts')}</TableCell>
                <TableCell align="right">{t('col_actions')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {jobs.map((job) => (
                <CatalogPdfTableRow
                  key={job.id}
                  row={job}
                  onRetry={handleGenerate}
                  retryDisabled={buttonDisabled}
                />
              ))}

              {!isLoading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack
                      sx={{
                        alignItems: 'center',
                        py: 6,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('no_jobs')}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}

              {isLoading && jobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Stack
                      sx={{
                        alignItems: 'center',
                        py: 6,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </PageContainer>
  );
}
